/** Profile number explainer: server-built, deterministic content. No model call on open. */

export type NumberType = 'life_path' | 'birth_day' | 'personal_year' | 'personal_month' | 'personal_day' | 'matrix22_center';
export type ExplainerIntent = 'money' | 'career' | 'relationships' | 'self' | 'decision';

export interface NumbersExplainerItem {
  numberType: NumberType;
  label: string;
  value: number;
  system: 'classical' | 'matrix22';
  systemLabel: string;
  role: string;
  meaning: string | null;
  meaningStatus: 'approved' | 'calculation_role_only' | 'no_approved_card';
  note: string | null;
  sourceCardIds: string[];
  calculationTrace?: string[];
}

export interface NumbersExplainer {
  version: string;
  title: string;
  subtitle: string;
  items: NumbersExplainerItem[];
  bridge: { heading: string; question: string; angleType: string; intentCategory: ExplainerIntent; cta: string };
}

export interface DisplayedNumbers {
  lifePath: { value: number };
  birthDay: { raw: number };
  cycles: { personalYear: number; personalMonth: number; personalDay: number };
  matrix22: { center: number };
}

export const NUMBERS_EXPLAINER_SURFACE = 'numbers_explainer' as const;

/** The six values the «Твоя карта» orbit renders, in visual order. */
export function displayedNumbers(highlights: DisplayedNumbers): Array<{ numberType: NumberType; value: number }> {
  return [
    { numberType: 'life_path', value: highlights.lifePath.value },
    { numberType: 'birth_day', value: highlights.birthDay.raw },
    { numberType: 'personal_year', value: highlights.cycles.personalYear },
    { numberType: 'personal_month', value: highlights.cycles.personalMonth },
    { numberType: 'personal_day', value: highlights.cycles.personalDay },
    { numberType: 'matrix22_center', value: highlights.matrix22.center },
  ];
}

/** True only when the explainer explains exactly the numbers on screen. */
export function explainerMatchesDisplayed(explainer: NumbersExplainer | null | undefined, highlights: DisplayedNumbers): boolean {
  if (!explainer || !Array.isArray(explainer.items)) return false;
  const shown = displayedNumbers(highlights);
  return explainer.items.length === shown.length
    && shown.every((item, index) => explainer.items[index]?.numberType === item.numberType && explainer.items[index]?.value === item.value);
}

/** Analytics fields only: no question text, names, dates or interpretation text. */
export function explainerAnalytics(bridge: NumbersExplainer['bridge'] | null | undefined): {
  sourceSurface: typeof NUMBERS_EXPLAINER_SURFACE; angleType?: string; intentCategory?: ExplainerIntent;
} {
  return {
    sourceSurface: NUMBERS_EXPLAINER_SURFACE,
    ...(bridge?.angleType ? { angleType: bridge.angleType } : {}),
    ...(bridge?.intentCategory ? { intentCategory: bridge.intentCategory } : {}),
  };
}

/** Route state for the existing Numerologist flow; it decides free, paid or chooser. */
export function explainerContinuationState(bridge: NumbersExplainer['bridge']): {
  question: string; autoSend: true; sourceSurface: typeof NUMBERS_EXPLAINER_SURFACE; angleType: string; questionRank: 'primary';
} {
  return {
    question: bridge.question,
    autoSend: true,
    sourceSurface: NUMBERS_EXPLAINER_SURFACE,
    angleType: bridge.angleType,
    questionRank: 'primary',
  };
}

export function chatOpenedEventFor(sourceSurface: string | undefined): 'chat_opened_from_numbers_explainer' | 'chat_opened_from_continuation' {
  return sourceSurface === NUMBERS_EXPLAINER_SURFACE ? 'chat_opened_from_numbers_explainer' : 'chat_opened_from_continuation';
}
